import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  Grid,
  List,
  ListItemButton,
  ListItemText,
  Checkbox,
  Divider,
  Button,
  Paper,
  TextField,
  CircularProgress,
  Fade,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import SmartTableCard from "utils/Smart Table/SmartTableCard";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import { api } from "utils/Api";
import { showSuccess } from "utils/messageAlerts";
import { useHierarchy } from "context/HierarchyContext";
import { getSecureItem } from "utils/SecureStorage";

function AccessControl() {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [entities, setEntities] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [accessControlData, setAccessControlData] = useState([]);
  const [originalPermissions, setOriginalPermissions] = useState({});
  const organization_id = JSON.parse(getSecureItem("currentUser"))?.organization_id;

  useEffect(() => {
    entityFetch();
    fetchRoles();
    fetchAllPermissions();
  }, []);

  useEffect(() => {
    if (!selectedRole || !accessControlData.length || !entities.length) return;

    const rolePermissions = accessControlData.filter((item) => item.role_id === selectedRole.id);

    const formatted = {};

    rolePermissions.forEach((perm) => {
      const actions = [];

      if (perm.can_read) actions.push("READ");
      if (perm.can_create) actions.push("CREATE");
      if (perm.can_update) actions.push("UPDATE");
      if (perm.can_delete) actions.push("DELETE");

      formatted[perm.entity_id] = actions;
    });

    const cloned = structuredClone(formatted);

    setPermissions(cloned);
    setOriginalPermissions(structuredClone(cloned));
  }, [selectedRole, accessControlData, entities]);

  const fetchAllPermissions = async () => {
    try {
        const data = await api.accessControl.getAccessControl({organization_id});
        setAccessControlData(data);
    } catch (error) {}
  };

  const entityFetch = async () => {
    try {
      const data = await api.accessControl.getEnumsEntity();
      setEntities(data || []);
    } catch (error) {}
  };

  const fetchRoles = async () => {
    try {
      const data = await api.roles.getRoles();
      const filtered = data.filter((role) => role.id > 2);

      setRoles(filtered);

      if (filtered.length > 0) {
        setSelectedRole(filtered[0]);
      }
    } catch (error) {
    }
  };

  const filteredEntities = useMemo(() => {
    return entities
      .filter((e) => (e.name !== "Organization") && (e.name !== "User Organization"))
      .filter((e) => e.name.toLowerCase().includes(search.toLowerCase()));
  }, [entities, search]);

  const hasChanges = useMemo(() => {
    return JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
  }, [permissions, originalPermissions]);

  const handleChange = (entityId, action) => {
    setPermissions((prev) => {
      const current = prev[entityId] || [];
      const exists = current.includes(action);

      return {
        ...prev,
        [entityId]: exists ? current.filter((a) => a !== action) : [...current, action],
      };
    });
  };

  const isChecked = (entityId, action) => {
    return !!permissions[entityId]?.includes(action);
  };

  const getRoleDisplayName = (roleName) => {
    if (roleName === "Staff") return "Concierge";
    if (roleName === "Tenant") return "Residents";
    return roleName;
  };

  const handleSave = async () => {
    if (!selectedRole) return;

    setSaving(true);

    try {
      // Convert permissions object into array format required by backend
      const payload = entities.map((entity) => {
        const actions = permissions[entity.id] || [];

        // find existing record for this role + entity
        const existingRecord = accessControlData.find(
          (item) => item.role_id === selectedRole.id && item.entity_id === entity.id
        );

        return {
          id: existingRecord?.id || null, // include id if exists
          role_id: selectedRole.id,
          entity_id: entity.id,
          can_create: actions.includes("CREATE"),
          can_read: actions.includes("READ"),
          can_update: actions.includes("UPDATE"),
          can_delete: actions.includes("DELETE"),
        };
      });

      console.log("Sending payload:", payload);

      await api.accessControl.updateAccessControl(payload);
      showSuccess("Permissions updated successfully!");
      fetchAllPermissions(); // Refresh data after update
    } catch (error) {
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box p={2} sx={{ backgroundColor: "#f4f6f8", minHeight: "100vh" }}>
        <Card sx={{ p: 4, borderRadius: 4, boxShadow: 3 }}>
          <Typography variant="h4" fontWeight="bold">
            Access Control Management
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3}>
            Configure role-based permissions securely.
          </Typography>

          <Divider sx={{ mb: 4 }} />

          <Grid container spacing={4}>
            {/* ROLES */}
            <Grid item xs={3}>
              <Paper sx={{ p: 1, borderRadius: 3 }}>
                <Typography fontWeight="bold" mb={2}>
                  Roles
                </Typography>

                <List>
                  {roles
                    .filter((role) => role.id > 2)
                    .map((role) => (
                      <ListItemButton
                        key={role.id}
                        selected={selectedRole?.id === role.id}
                        onClick={() => setSelectedRole(role)}
                        sx={{ borderRadius: 2, mb: 1 }}
                      >
                        <ListItemText primary={getRoleDisplayName(role.name)} />
                      </ListItemButton>
                    ))}
                </List>
              </Paper>
            </Grid>

            {/* PERMISSIONS */}
            <Grid item xs={9}>
              <Fade in={!loading} timeout={400}>
                <Box>
                  <SmartTableCard
                    title={`Permissions for ${getRoleDisplayName(selectedRole?.name || "")}`}
                    headerRight={[
                      <TextField
                        key="search"
                        size="small"
                        placeholder="Search entity..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ width: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />,
                    ]}
                    columns={[
                      { label: "Entity", key: "Entity", align: "left" },
                      // { label: "READ", key: "READ", align: "center" },
                      { label: "CREATE", key: "CREATE", align: "center" },
                      { label: "UPDATE", key: "UPDATE", align: "center" },
                      { label: "DELETE", key: "DELETE", align: "center" },
                    ]}
                    rows={filteredEntities.map((entity) => ({
                      Entity: (
                        <SoftTypography variant="button" fontWeight="medium">
                          {entity.name}
                        </SoftTypography>
                      ),

                      // READ: (
                      //   <IconButton onClick={() => handleChange(entity.id, "READ")} size="small">
                      //     {isChecked(entity.id, "READ") ? (
                      //       <CheckIcon color="success" />
                      //     ) : (
                      //       <CloseIcon color="error" />
                      //     )}
                      //   </IconButton>
                      // ),

                      CREATE: (
                        <IconButton onClick={() => handleChange(entity.id, "CREATE")} size="small">
                          {isChecked(entity.id, "CREATE") ? (
                            <CheckIcon color="success" />
                          ) : (
                            <CloseIcon color="error" />
                          )}
                        </IconButton>
                      ),

                      UPDATE: (
                        <IconButton onClick={() => handleChange(entity.id, "UPDATE")} size="small">
                          {isChecked(entity.id, "UPDATE") ? (
                            <CheckIcon color="success" />
                          ) : (
                            <CloseIcon color="error" />
                          )}
                        </IconButton>
                      ),

                      DELETE: (
                        <IconButton onClick={() => handleChange(entity.id, "DELETE")} size="small">
                          {isChecked(entity.id, "DELETE") ? (
                            <CheckIcon color="success" />
                          ) : (
                            <CloseIcon color="error" />
                          )}
                        </IconButton>
                      ),
                    }))}
                  />

                  {/* Buttons */}
                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button variant="contained" onClick={handleSave} disabled={saving || !hasChanges}>
                      {saving ? <CircularProgress size={20} /> : "Save Changes"}
                    </Button>
                  </Box>
                </Box>
              </Fade>

              {loading && (
                <Box textAlign="center" mt={5}>
                  <CircularProgress />
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      </Box>
    </DashboardLayout>
  );
}

export default AccessControl;